import { useEffect, useState } from "react";
import { Route, Routes, useNavigate, useParams } from "react-router-dom";

import { Form, FormikErrors } from "formik";

import { Link } from "components/elements/Link/Link";
import { FormPagination, FormStatus } from "components/form";
import { TabContent } from "components/layout";
import { ConfirmModal } from "components/modals";
import { RestrictedArea } from "containers/Permissions";
import {
  useCreateVariant,
  useUpdateVariant,
  useDeleteVariantDraft,
  useDeleteVariantVersionDraft,
  useUpdateVariantDraft,
  useUpdateVariantVersionDraft,
  useVariantDraft,
  useVariantVersionDraft,
  ApiServerErrorMetaMatchingVariantVersion,
} from "hooks/tivoApps";
import { useToggle } from "hooks/utils";
import { ApiRequestError } from "types/api";
import { getCountries } from "utils/continents";
import { pathToUrl } from "utils/url";
import { isTabInvalid } from "utils/validation";

import { DEFAULT_PATHS, PATHS } from "../constants";
import { VARIANT_TABS, TEXTS } from "./constants";
import { VARIANT_FIELDS } from "./fields";
import { useVariantFormData } from "./hooks/useVariantFormData";
import { TiVoAppFormik } from "./TiVoAppFormik";
import {
  createTabLink,
  getActiveTab,
  TiVoAppFormTabs,
} from "./TiVoAppFormTabs";
import { VariantForm } from "./types";
import {
  adaptVariantFormToRequest,
  getVariantFormInitialValues,
  useFormMode,
} from "./utils";
import { createVariantValidationSchema } from "./validation";

const CONFLICT_STATUS = "409";

const useVariantDraftQueries = (id?: string, variantId?: string) => {
  const variantDraft = useVariantDraft(id);
  const updateVariantDraft = useUpdateVariantDraft(id);
  const deleteVariantDraft = useDeleteVariantDraft(id);

  const variantVersionDraft = useVariantVersionDraft(id, variantId);
  const updateVariantVersionDraft = useUpdateVariantVersionDraft(id, variantId);
  const deleteVariantVersionDraft = useDeleteVariantVersionDraft(id, variantId);

  return {
    variantDraftData: variantId
      ? variantVersionDraft?.data
      : variantDraft?.data,
    updateVariantDraft: variantId
      ? updateVariantVersionDraft
      : updateVariantDraft,
    deleteVariantDraft: variantId
      ? deleteVariantVersionDraft
      : deleteVariantDraft,
  };
};

export function TiVoVariantForm() {
  const mode = useFormMode();
  const navigate = useNavigate();
  const params = useParams();
  const { id, variantId, versionId, tabId } = params;

  /** Variants */
  const { providerData, variantData, isLoading, error, selectedVersion } =
    useVariantFormData(id, variantId, versionId);
  const countries = getCountries();
  const createVariant = useCreateVariant(id);
  const updateVariant = useUpdateVariant(id, variantId);

  /** Variant Drafts */
  const { variantDraftData, updateVariantDraft, deleteVariantDraft } =
    useVariantDraftQueries(id, variantId);
  const handleSubmitDraft = (values: VariantForm) => {
    const data = adaptVariantFormToRequest(values, countries);
    updateVariantDraft.mutate(data);
  };

  /** Modals */
  const [isDiscardModalOpen, toggleDiscardModal] = useToggle(false);
  const [isConflictModalOpen, toggleConflictModal] = useToggle(false);
  const [pendingSubmitValues, setPendingSubmitValues] =
    useState<VariantForm | null>(null);

  const hasDraft = Boolean(variantDraftData?.results);

  const pageHeader =
    mode === "createVariant"
      ? `${providerData?.results?.label} | New variant`
      : `${providerData?.results?.label} | ${variantData?.results?.label || "App variant"}`;

  const activeTab = getActiveTab(VARIANT_TABS, params);

  const redirectToInvalidTab = (errors: FormikErrors<VariantForm>) => {
    const firstInvalidTab = VARIANT_TABS.find(({ fields }) =>
      isTabInvalid(fields, errors)
    );

    if (!firstInvalidTab) {
      return;
    }

    navigate(
      createTabLink({
        id,
        mode,
        tabId: firstInvalidTab.path,
        variantId,
        versionId,
      })
    );
  };
  // Non-existing tab should redirect to 404
  useEffect(() => {
    if (
      Boolean(params["*"]) &&
      !VARIANT_TABS.some((tab) => tab.path === params["*"])
    ) {
      navigate("/404");
    }
  }, [navigate, params]);

  /** Form Methods */
  const handleSubmit = async (values: VariantForm, strict = true) => {
    const data = adaptVariantFormToRequest(values, countries);

    const cleanupDraft = async () => {
      if (hasDraft) {
        try {
          await deleteVariantDraft.mutateAsync();
          updateVariantDraft.reset();
        } catch (e) {
          console.error("Failed to delete the draft.", e);
        }
      }
    };

    const onSuccess = (newVariantId?: string) => {
      navigate(
        pathToUrl(PATHS.detailsVariant, {
          id: id,
          variantId: newVariantId || variantId,
          tabId: "",
        })
      );
    };

    const handleConflictError = (
      err: ApiRequestError<ApiServerErrorMetaMatchingVariantVersion>
    ) => {
      const hasConflictErrorRes = err.serverErrors?.some(
        (error) => `${error?.status}` === CONFLICT_STATUS
      );

      if (strict && hasConflictErrorRes) {
        setPendingSubmitValues(values);
        toggleConflictModal();
      }
    };

    if (mode === "editVariant") {
      updateVariant.mutate(
        { ...data, strict },
        {
          onSuccess: async () => {
            await cleanupDraft();
            onSuccess();
          },
          onError: handleConflictError,
        }
      );
      return;
    }
    createVariant.mutate(
      { ...data, strict },
      {
        onSuccess: async (variant) => {
          await cleanupDraft();
          onSuccess(variant.id);
        },
        onError: handleConflictError,
      }
    );
  };

  useEffect(() => {
    const needsRedirect =
      mode === "editVariant" && versionId === "latest" && selectedVersion;

    if (needsRedirect) {
      navigate(
        createTabLink({
          id,
          mode,
          tabId: tabId || DEFAULT_PATHS.VARIANT_FORM,
          variantId,
          versionId: selectedVersion.id,
        }),
        { replace: true }
      );
    }
  }, [versionId, selectedVersion, id, mode, tabId, variantId, navigate]);

  // We can pass only one element as this array lists all existing matching variants
  // If the variants are exactly the same, we can present user only one option
  const conflictError =
    createVariant.error?.serverErrors?.find(
      (error) => `${error?.status}` === CONFLICT_STATUS
    )?.meta?.matchingVariantVersions?.[0] ?? null;

  const conflictDescription = (
    <>
      {TEXTS.VARIANT_VERSION_CONFLICT.DESCRIPTION}
      {conflictError?.variantVersionId && (
        <>
          {TEXTS.VARIANT_VERSION_CONFLICT.MATCHING_VARIANTS}
          <Link
            onClick={() => {
              toggleConflictModal();
              createVariant.reset();
              navigate(
                pathToUrl(PATHS.editVariant, {
                  id,
                  variantId: conflictError.variantId,
                  versionId: conflictError.variantVersionId,
                  tabId: DEFAULT_PATHS.VARIANT_FORM,
                })
              );
            }}
          >
            {conflictError.variantVersionLabel}
          </Link>
        </>
      )}
    </>
  );

  return (
    <TiVoAppFormik
      header={pageHeader}
      loading={isLoading}
      error={error}
      initialValues={getVariantFormInitialValues(
        providerData?.results,
        hasDraft ? variantDraftData?.results : selectedVersion,
        countries
      )}
      onSubmit={(values) => handleSubmit(values)}
      validationSchema={createVariantValidationSchema(VARIANT_FIELDS)}
    >
      {({ values, validateForm, handleSubmit: formikHandleSubmit }) => {
        return (
          <>
            <TiVoAppFormTabs tabs={VARIANT_TABS} />
            <TabContent>
              <Form>
                <Routes>
                  {VARIANT_TABS.map(({ Component, path, allowed }) => (
                    <Route
                      key={path}
                      path={path}
                      element={
                        <RestrictedArea key={path} allowed={allowed}>
                          <Component />
                        </RestrictedArea>
                      }
                    />
                  ))}
                </Routes>
              </Form>
            </TabContent>
            <FormStatus
              draftMutations={[updateVariantDraft, deleteVariantDraft]}
              submitMutations={[createVariant, updateVariant]}
              withErrorDetails
            />
            <FormPagination
              activeTab={activeTab}
              discardLabel={TEXTS.DRAFT.REMOVE_DRAFT}
              submitLabel={TEXTS.SHARED.SAVE_BUTTON_LABEL}
              tabCount={VARIANT_TABS.length}
              tabTitle={VARIANT_TABS[activeTab].text}
              onDiscard={hasDraft ? toggleDiscardModal : undefined}
              onSubmit={async () => {
                const errors = await validateForm();
                redirectToInvalidTab(errors);
                formikHandleSubmit();
              }}
              onSave={() => handleSubmitDraft(values)}
              hasDraft={hasDraft}
              saving={
                createVariant.isPending ||
                updateVariant.isPending ||
                updateVariantDraft.isPending
              }
            />
            <ConfirmModal
              isOpen={isDiscardModalOpen}
              onClose={toggleDiscardModal}
              isSubmiting={deleteVariantDraft.isPending}
              onConfirm={() =>
                deleteVariantDraft.mutate(undefined, {
                  onSuccess: () => {
                    updateVariantDraft.reset();
                    toggleDiscardModal();
                  },
                })
              }
              cancelLabel={TEXTS.DRAFT.KEEP_DRAFT}
              confirmLabel={TEXTS.DRAFT.REMOVE_DRAFT}
              title={TEXTS.DRAFT.MODAL_TITLE}
              description={TEXTS.DRAFT.MODAL_DESC}
            />
            <ConfirmModal
              isOpen={isConflictModalOpen}
              onClose={() => {
                setPendingSubmitValues(null);
                toggleConflictModal();
              }}
              isSubmiting={createVariant.isPending}
              onConfirm={() => {
                if (!pendingSubmitValues) {
                  toggleConflictModal();
                  return;
                }

                toggleConflictModal();
                handleSubmit(pendingSubmitValues, false);
              }}
              cancelLabel={TEXTS.VARIANT_VERSION_CONFLICT.CANCEL}
              confirmLabel={TEXTS.VARIANT_VERSION_CONFLICT.CONFIRM}
              title={TEXTS.VARIANT_VERSION_CONFLICT.TITLE}
              description={conflictDescription}
              size="medium"
            />
          </>
        );
      }}
    </TiVoAppFormik>
  );
}
